import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { Tooltip } from '../../../Tooltip/Tooltip';
import { ChipWithTooltip } from '../../../Chip/ChipWithTooltip';
import { SystemIcon } from '../../../Icon/SystemIcon';
import { useDataTableFilterContext } from '../../utils/DataTableFilterContext';
import { useDataTableQuickReportsContext } from '../../utils/DataTableQuickReportsContext';
import { QueryModel } from '../../utils/DataTableQueryProvider/DataTableQuery.types';
import { OnQuickReportsChanged } from '../../utils/DataTableTypes';
import { getActiveQuickReport } from '../DataTableQuickReports/utils/QuickReports.utils';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { useDataTableQueryContext } from '../../utils/DataTableQueryProvider/DataTableQueryProvider';

interface Props {
  dataTest?: string;
  hasSearch: boolean;
  onSearchClear?: () => void;
}

export const DataTableActiveFilters: FC<React.PropsWithChildren<Props>> = ({
  dataTest = 'data-table-active-reports',
  hasSearch,
  onSearchClear
}) => {
  const { setOpenFilterDrawer, hasFilters, visibleFilters, activeFilters, clearFilterAll } =
    useDataTableFilterContext();

  const { queryVariables, applyQuery } = useDataTableQueryContext();
  const { intl, format } = useIntlUds();
  const { activeQuickReportId, quickReports, setActiveQuickReportId, unSavedQuickReport } =
    useDataTableQuickReportsContext();

  const activeQuickReport = getActiveQuickReport(quickReports, activeQuickReportId);

  const onDeleteFilterHandler = useCallback(
    (e: any) => {
      e.stopPropagation();
      if (activeQuickReportId) {
        setActiveQuickReportId(undefined);
      } else {
        clearFilterAll();
      }
    },
    [activeQuickReportId, clearFilterAll, setActiveQuickReportId]
  );

  const hasSelectedFilters = hasFilters && (visibleFilters === 'all' || visibleFilters?.length > 0);
  // Check if filters are not set, search is not used and active quick reports doesn't exist
  if (!hasSelectedFilters && !queryVariables?.query?.search && !activeQuickReport) return null;

  const activeFilterLabelList = activeFilters.map((filter) => filter.label);

  const searchLabel = format(intl.DataTable.Filters.Active.search, { searchTerm: queryVariables?.query?.search ?? '' });
  const activeFiltersLabel = format(intl.DataTable.Filters.Active.activeFilters, {
    activeFiltersLength: activeFilterLabelList.length?.toString(),
    filters: activeFilterLabelList.join(', ')
  });

  return (
    <StyledChipWrapper>
      {(activeFilterLabelList.length > 0 || activeQuickReport) && (
        <StyledChipWithTooltip
          data-test={activeQuickReport ? 'data-table-active-quick-report' : dataTest}
          maxWidth="300px"
          onClick={() => setOpenFilterDrawer(true)}
          toolTipText={activeQuickReport?.name || activeFilterLabelList.join(',')}
          label={getFiltersLabel(activeFiltersLabel, activeQuickReport, !!unSavedQuickReport)}
          onDelete={onDeleteFilterHandler}
        />
      )}
      {!!queryVariables?.query?.search && hasSearch && (
        <StyledSearchChip
          data-test="data-table-search-chip"
          maxWidth="300px"
          deleteDataTest="data-table-search-chip-delete"
          toolTipText={queryVariables?.query?.search}
          onDelete={() => {
            onSearchClear?.();
            applyQuery({ search: '' }, { mode: 'patch', persist: true });
          }}
          label={searchLabel}
        />
      )}
    </StyledChipWrapper>
  );
};

const StyledChipWrapper = styled.div`
  display: flex;
  padding-left: ${({ theme }) => theme.spacingSize.l};
`;

const StyledChipWithTooltip = styled(ChipWithTooltip)`
  cursor: pointer;
  margin-right: ${({ theme }) => theme.spacingSize.l};
  height: 35px;
  .text {
    display: flex;
    align-items: center;

    > span {
      margin-right: ${({ theme }) => theme.spacingSize.m};
    }
  }
`;

const StyledSearchChip = styled(ChipWithTooltip)`
  cursor: pointer;
  height: 35px;
`;

export const getFiltersLabel = (
  label: string,
  quickReport: OnQuickReportsChanged<QueryModel, string, unknown, string> | undefined,
  isQuickReportUnSaved: boolean
): JSX.Element => {
  if (!quickReport) {
    return <>{label}</>;
  }
  return (
    <>
      {isQuickReportUnSaved && (
        <Tooltip title="Unsaved changes">
          <SystemIcon name="warning" size="small" dataTest="un-saved-quick-report-icon" />
        </Tooltip>
      )}
      {quickReport.name}
    </>
  );
};
